import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ObservableRequestService } from '../../services/observable-request.service';
import { ConstantsService } from '../../services/constants-service';
import { SnackBarService } from '../../services/snackbar.service';

interface ClientWithPendingTransactions {
  clientId: number;
  clientName: string;
  /** True for WTT clients; false or absent for WWFT (matches organisation client header). */
  isWTT?: boolean;
  pendingTransactionCount: number;
  financeManager?: string;
  financeManagerName?: string;
  entityId?: number;
}

type ClientTypePendingFilter = 'all' | 'wtt' | 'wwft';

@Component({
  selector: 'app-pending-transactions-overview',
  templateUrl: './pending-transactions-overview.component.html',
  styleUrls: ['./pending-transactions-overview.component.css']
})
export class PendingTransactionsOverviewComponent implements OnInit {
  clients: ClientWithPendingTransactions[] = [];
  loading = false;
  exporting = false;
  totalClients = 0;
  /** When true (default), show only transactions open more than two weeks */
  onlyOpenMoreThanTwoWeeks = true;
  /** Filter list by WTT vs WWFT (applied client-side on loaded data). */
  clientTypeFilter: ClientTypePendingFilter = 'all';

  constructor(
    private requestService: ObservableRequestService,
    private snackBarService: SnackBarService,
    private router: Router
  ) {}

  ngOnInit() {
    this.loadClientsWithPendingTransactions();
  }

  loadClientsWithPendingTransactions() {
    this.loading = true;
    const url = `${ConstantsService.TRANSACTION}/clients-with-pending?onlyOpenMoreThanTwoWeeks=${this.onlyOpenMoreThanTwoWeeks}`;
    
    this.requestService.getRequest(url).subscribe(
      (response: any) => {
        this.loading = false;
        try {
          // Handle ResponseVO format - ResponseVO has arrResult property
          if (response && response.arrResult) {
            const result = response.arrResult;
            // arrResult is the Map<String, Object> we returned
            if (result && Array.isArray(result.clients)) {
              this.clients = result.clients;
              this.totalClients = result.totalClients || result.clients.length;
            } else if (result && typeof result === 'object') {
              // If arrResult is the object directly
              this.clients = result.clients || [];
              this.totalClients = result.totalClients || 0;
            } else {
              this.clients = [];
              this.totalClients = 0;
            }
          } else if (response && Array.isArray(response.clients)) {
            // Handle direct array format (unlikely but possible)
            this.clients = response.clients;
            this.totalClients = response.totalClients || response.clients.length;
          } else if (response && response.clients && typeof response.clients === 'object') {
            // Handle if clients is an object (shouldn't happen but be safe)
            this.clients = [];
            this.totalClients = 0;
          } else {
            this.clients = [];
            this.totalClients = 0;
          }
        } catch (e) {
          console.error('Error parsing response:', e, response);
          this.clients = [];
          this.totalClients = 0;
          this.snackBarService.showSimpleMsj('Error parsing response: ' + (e.message || 'Unknown error'));
        }
      },
      error => {
        this.loading = false;
        console.error('Error loading clients with pending transactions:', error);
        this.snackBarService.showSimpleMsj('Error loading clients with pending transactions: ' + (error.message || 'Unknown error'));
        this.clients = [];
        this.totalClients = 0;
      }
    );
  }

  navigateToClient(client: ClientWithPendingTransactions) {
    if (client.entityId) {
      // Navigate to the client's transaction monitoring tab
      this.router.navigate(['/entity', client.entityId, 'transaction-monitoring']);
    } else {
      this.snackBarService.showSimpleMsj('Cannot navigate to client: entity ID not available');
    }
  }

  refresh() {
    this.loadClientsWithPendingTransactions();
  }

  onFilterChange() {
    this.loadClientsWithPendingTransactions();
  }

  exportPendingTransactions() {
    if (this.filteredClients.length === 0) {
      this.snackBarService.showSimpleMsj('No data to export for the current filters');
      return;
    }

    this.exporting = true;
    const url = `${ConstantsService.PENDING_TRANSACTIONS_EXPORT}?onlyOpenMoreThanTwoWeeks=${this.onlyOpenMoreThanTwoWeeks}&clientType=${this.clientTypeFilter}`;

    this.requestService.downloadFile(url).subscribe(
      (blob: Blob) => {
        const dateStamp = new Date().toISOString().slice(0, 10);
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `pending_transactions_${dateStamp}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
        this.exporting = false;
      },
      error => {
        this.exporting = false;
        console.error('Error exporting pending transactions:', error);
        this.snackBarService.showSimpleMsj('Error exporting pending transactions: ' + (error.message || 'Unknown error'));
      }
    );
  }

  get filteredClients(): ClientWithPendingTransactions[] {
    if (this.clientTypeFilter === 'all') {
      return this.clients;
    }
    if (this.clientTypeFilter === 'wtt') {
      return this.clients.filter(c => c.isWTT === true);
    }
    return this.clients.filter(c => c.isWTT !== true);
  }

  clientTypeLabel(client: ClientWithPendingTransactions): string {
    return client.isWTT === true ? 'WTT' : 'WWFT';
  }
}



// WEBPACK FOOTER //
// ./src/app/finance/pending-transactions-overview/pending-transactions-overview.component.ts